import { getCookie, setCookie } from 'cookies-next/client';

import {
  COOKIE_ZENDESK_SSO_STATE,
  COOKIE_ZENDESK_SSO_STATE_OPTIONS,
  DUMMY_URL_SCHEME,
  HELP_BASE_PATH,
  JWT_PARAM,
  LOGIN_PARAM,
  RETURN_TO_PARAM,
  ZD_SSO_PARAM,
  ZENDESK_HOME_URL,
  ZENDESK_JWT_URL,
  ZENDESK_SSO_PATH,
} from './constants';

import { getPersistentId } from '@/modules/activityTracker/helpers.ts';
import { bucketUserExperiments } from '@/modules/experiments/api.ts';
import {
  ON_IDENTIFIER,
  OPTIMIZELY_EXPERIMENTS,
  type OpzlyExperimentName,
  ZENDESK_EXPERIMENT_TAG,
} from '@/modules/experiments/config.ts';
import { SERVERSIDE_getCountryCode } from '@/modules/experiments/helpers/SERVERSIDE_getCountryCode.ts';
import { SERVERSIDE_getReferer } from '@/modules/experiments/helpers/SERVERSIDE_getReferer.ts';
import { checkVariantMismatches } from '@/modules/experiments/helpers/checkVariantMismatches.ts';
import { getExperimentsDecision } from '@/modules/experiments/helpers/getExperimentsDecision.ts';
import { getPopulatedPreviewExperiments } from '@/modules/experiments/helpers/getPopulatedPreviewExperiments.ts';
import { logBucketingError } from '@/modules/experiments/helpers/logBucketingError.ts';
import { type CachedExperiments } from '@/modules/experiments/types.ts';
import { getLogger } from '@/modules/observability/logging.ts';
import { safeJsonParse } from '@/modules/storage/helpers.ts';
import { SERVERSIDE_getUserData } from '@/modules/user/SERVERSIDE_getUserData.tsx';
import { type User } from '@/modules/user/aliases.ts';
import { getZendeskJwt } from '@/modules/zendesk/api.ts';
import { type ZendeskSSOState } from '@/modules/zendesk/types.ts';

export function getHelpUrl(articleId: string): string {
  const base = new URL(HELP_BASE_PATH, DUMMY_URL_SCHEME);
  const article = new URL(articleId + '/', base);
  return article.pathname;
}

/**
 * Navigates to a zendesk help centre url using a router push, adding SSO jwts if we can.
 *
 * This redirects to the route defined at /zendesk/sso,
 * which gets the JWT using the method above and does the actual redirect to ZD.
 *
 * @param {string} returnTo - The URL to redirect to after authentication
 */
export function getZendeskRedirectUrl(returnTo: string | null = null) {
  const redirectUrl = new URL(ZENDESK_SSO_PATH, DUMMY_URL_SCHEME);
  redirectUrl.searchParams.set(LOGIN_PARAM, 'false');
  if (returnTo) {
    redirectUrl.searchParams.set(RETURN_TO_PARAM, returnTo);
  }
  return redirectUrl.pathname + redirectUrl.search;
}

export function getZendeskLoginUrl(returnTo: string = ZENDESK_HOME_URL) {
  const loginUrl = new URL('login/', DUMMY_URL_SCHEME);
  loginUrl.searchParams.set(ZD_SSO_PARAM, 'true');
  loginUrl.searchParams.set(RETURN_TO_PARAM, returnTo);
  return loginUrl.pathname + loginUrl.search;
}

export async function getZendeskAuthUrl(returnTo: string = ZENDESK_HOME_URL) {
  const jwt = await getZendeskJwt();
  const authUrl = new URL(ZENDESK_JWT_URL);
  authUrl.searchParams.set(JWT_PARAM, jwt.data.jwt);
  authUrl.searchParams.set(RETURN_TO_PARAM, returnTo);
  return authUrl.toString();
}

export function setZendeskSSOStateCookieValue(returnTo: string) {
  const state: ZendeskSSOState = { returnTo };
  setCookie(
    COOKIE_ZENDESK_SSO_STATE,
    JSON.stringify(state),
    COOKIE_ZENDESK_SSO_STATE_OPTIONS
  );
}

/**
 * Checks the URL parameters for Zendesk SSO information and sets
 * a cookie to maintain it through the login flow
 *
 * @param {URLSearchParams} searchParams - Next.js search parameters
 * @returns {boolean} - Whether Zendesk SSO state was found and stored
 */
export function setZendeskSSOStateCookie(searchParams: {
  get: (key: string) => string | null;
}): boolean {
  const isZendeskSSO = searchParams.get(ZD_SSO_PARAM) === 'true';
  const returnTo = searchParams.get(RETURN_TO_PARAM);
  if (isZendeskSSO && returnTo) {
    setZendeskSSOStateCookieValue(returnTo);
    return true;
  }
  return false;
}

/**
 * Helper function to get Zendesk SSO state from cookies
 *
 * @returns The return_to URL or null if not found
 */
export function getZendeskSSOStateValue(): string | null {
  const cookieValue = getCookie(COOKIE_ZENDESK_SSO_STATE) as string | undefined;
  if (!cookieValue) {
    return null;
  }
  const state: ZendeskSSOState = safeJsonParse<{ return_to: string }>(
    cookieValue,
    null
  );
  return state?.returnTo || null;
}

async function getPreviewExperiments(user: User | null) {
  const countryCode = await SERVERSIDE_getCountryCode();
  return getPopulatedPreviewExperiments(user, countryCode);
}

async function doBucketUserExperiments(
  experimentNames: OpzlyExperimentName[],
  cachedExps: CachedExperiments
) {
  const persistentId = (await getPersistentId()) as string;
  const countryCode = await SERVERSIDE_getCountryCode();
  try {
    const res = await bucketUserExperiments({
      experimentNames,
      countryCode,
      persistentId,
      date: Date.now(),
      referer: await SERVERSIDE_getReferer(),
    });
    checkVariantMismatches(cachedExps, res.data);
  } catch (error: unknown) {
    logBucketingError(experimentNames, error);
  }
}

function isAuthenticatedUser(user: User | null) {
  return Boolean(user && Object.keys(user).length);
}

/**
 * Fetches a list of active Zendesk experiments where the decision value is set to 'variant'.
 *
 * The method retrieves Zendesk-related experiments from a predefined object of experiments,
 * then fetches their decision state from the server. Only experiments with a decision value
 * of 'variant' are included in the resulting list.
 *
 * @return {Promise<string[]>} A promise that resolves to an array of experiment names
 * classified as active based on their decision state.
 */
export async function getActiveZendeskExperiments() {
  const user = await SERVERSIDE_getUserData();
  try {
    const experiments = Object.entries(OPTIMIZELY_EXPERIMENTS)
      // Filter our list of experiments down to only those tagged with the Zendesk experiment tag
      .filter(
        ([, value]) =>
          'metadata' in value &&
          value?.metadata?.tags?.includes(ZENDESK_EXPERIMENT_TAG)
      )
      // Get the name of the experiments as an OpzlyExperimentName
      .map(([key]) => key as OpzlyExperimentName);
    // Fetch the previews for the active experiments, if any
    const previews = await getPreviewExperiments(user);
    if (previews) {
      const decisions = getExperimentsDecision(experiments, previews);
      // only bucket if we have an authenticated user.
      if (isAuthenticatedUser(user)) {
        await doBucketUserExperiments(experiments, previews);
      }
      return (
        Object.entries(decisions)
          // Filter out the experiments that are not active ('on') based on their decision state
          // At the moment we only support targeted rollout with 'on' variants
          .filter(
            ([exp, dec]) =>
              experiments.includes(exp as OpzlyExperimentName) &&
              dec === ON_IDENTIFIER
          )
          // Map the experiment names to their public aliases, if available
          .map(([exp]) => {
            const expConfig =
              OPTIMIZELY_EXPERIMENTS[
                exp as keyof typeof OPTIMIZELY_EXPERIMENTS
              ];
            if ('metadata' in expConfig) {
              return expConfig.metadata.public_alias || exp;
            }
            return exp;
          })
      );
    }
    return [];
  } catch (error) {
    getLogger().error('Failed to fetch active zendesk experiments', {
      message: error,
    });
    return [];
  }
}
